import { ActionStatus } from 'mdello-client-ts';
import { Tag } from 'antd';
import React, { useState, useEffect } from 'react';
import Copy from 'src/component/Copy';
import InnerHeader from 'src/component/InnerHeader';
import NoxTable from 'src/component/Table';
import { SearchBy } from 'src/component/Table/enums/searchby.enum';
import formatDate from 'src/helpers/formatDate';
import { useDeleteTrainings } from 'src/reactQuery/requests/trainings/useDeleteRequests';
import { useGetTrainings } from 'src/reactQuery/requests/trainings/useGetRequests';
import { useUpdateTrainings } from 'src/reactQuery/requests/trainings/useUpdateRequests';

interface RequestTableProps {
  _id: string;
  category: 'training' | 'vacation';
  comments: string[];
  createdAt: Date;
  description: string;
  key: number;
  lector: string;
  status: ActionStatus;
  trainingRequestDate: Date;
  trainingTitle: string;
  type: 'ongoing' | 'history';
  updatedAt: Date;
  username: string;
}

interface TableActionsProps {
  onClick: () => void;
  title: string;
  type: string;
}

const Trainings = () => {
  const [requests, setRequests] = useState<RequestTableProps[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[] | undefined>([]);
  const { data: TrainingsRequestData, refetch } = useGetTrainings('training');
  const { mutate: deleteMutation } = useDeleteTrainings({
    onSuccess: () => {
      refetch();
    }
  });
  const { mutate: updateMutation } = useUpdateTrainings({
    onSuccess: () => {
      refetch();
    }
  });

  useEffect(() => {
    if (!TrainingsRequestData) {
      return;
    }

    // mapToTable
    const filteredRequests: RequestTableProps[] = TrainingsRequestData.data.map(
      (request, index) => {
        const { lector, trainingRequestDate, trainingTitle, type } = request.data;
        const {
          _id,
          category,
          createdAt,
          comments,
          description,
          username,
          updatedAt,
          status
        } = request;

        return {
          _id,
          category,
          comments,
          createdAt,
          description,
          key: index,
          lector,
          status,
          trainingRequestDate,
          trainingTitle,
          type,
          updatedAt,
          username
        };
      }
    );
    setRequests(filteredRequests);
  }, [TrainingsRequestData]);

  const columns = [
    {
      dataIndex: '_id',
      key: 'id',
      render: (_id: any) => {
        return <Copy text={_id} />;
      },
      sorter: (a: any, b: any) => a._id.localeCompare(b._id),
      title: 'Id'
    },
    {
      dataIndex: 'category',
      key: 'category',
      render: (category: any) => {
        return <Copy text={category} />;
      },
      sorter: (a: any, b: any) => a.category.toString().localeCompare(b.category),
      title: 'Category'
    },
    {
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (createdAt: any) => {
        return <Copy text={formatDate(createdAt)} />;
      },
      sorter: (a: any, b: any) => a.createdAt.toString().localeCompare(b.createdAt),
      title: 'Created at'
    },
    {
      dataIndex: 'description',
      key: 'description',
      render: (description: any) => {
        return <Copy text={description} />;
      },
      sorter: (a: any, b: any) => a.description.toString().localeCompare(b.description),
      title: 'Description'
    },
    {
      dataIndex: 'lector',
      key: 'lector',
      render: (lector: any) => {
        return <Copy text={lector} />;
      },
      sorter: (a: any, b: any) => a.lector.toString().localeCompare(b.lector),
      title: 'Lector'
    },
    {
      dataIndex: 'status',
      key: 'status',
      render: (status: any) => {
        let style = {};
        if (status === 'pending') {
          style = {
            backgroundColor: 'rgba(155, 160, 173, 0.15)',
            color: '#9BA0AD'
          };
        }
        if (status === 'confirmed') {
          style = {
            backgroundColor: 'rgba(65, 204, 156, 0.15)',
            color: '#41CC9C'
          };
        }
        if (status === 'rejected') {
          style = {
            backgroundColor: 'rgba(255, 71, 108, 0.15)',
            color: '#FF476C'
          };
        }
        return (
          <Tag
            key={status}
            style={{
              ...style,
              border: 'none',
              cursor: 'default',
              width: '90%'
            }}
          >
            {status}
          </Tag>
        );
      },
      sorter: (a: any, b: any) => a.status.toString().localeCompare(b.status),
      title: 'Status'
    },
    {
      dataIndex: 'trainingRequestDate',
      key: 'trainingRequestDate',
      render: (trainingRequestDate: any) => {
        return <Copy text={formatDate(trainingRequestDate)} />;
      },
      sorter: (a: any, b: any) =>
        a.trainingRequestDate.toString().localeCompare(b.trainingRequestDate),
      title: 'Training Request Date'
    },
    {
      dataIndex: 'trainingTitle',
      key: 'trainingTitle',
      render: (trainingTitle: any) => {
        return <Copy text={trainingTitle} />;
      },
      sorter: (a: any, b: any) => a.trainingTitle.toString().localeCompare(b.trainingTitle),
      title: 'Training Title'
    },
    {
      dataIndex: 'type',
      key: 'type',
      render: (type: any) => {
        return <Copy text={type} />;
      },
      sorter: (a: any, b: any) => a.type.toString().localeCompare(b.type),
      title: 'Type'
    },
    {
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (updatedAt: any) => {
        return <Copy text={formatDate(updatedAt)} />;
      },
      sorter: (a: any, b: any) => a.updatedAt.toString().localeCompare(b.updatedAt),
      title: 'Updated at'
    },
    {
      dataIndex: 'username',
      key: 'username',
      render: (username: any) => {
        return <Copy text={username} />;
      },
      sorter: (a: any, b: any) => a.username.toString().localeCompare(b.username),
      title: 'Username'
    }
  ];

  const onRequestDelete = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = TrainingsRequestData?.data[idx];

      if (item) {
        deleteMutation(item._id);
      }
    });

    setSelectedRowKeys([]);
  };
  const onApprove = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = TrainingsRequestData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.CONFIRMED
        });
      }
    });

    setSelectedRowKeys([]);
  };
  const onReject = () => {
    selectedRowKeys?.forEach((idx) => {
      const item = TrainingsRequestData?.data[idx];

      if (item) {
        updateMutation({
          requestId: item._id,
          status: ActionStatus.REJECTED
        });
      }
    });

    setSelectedRowKeys([]);
  };

  const actions: TableActionsProps[] = [];

  if (selectedRowKeys?.length) {
    actions.unshift(
      {
        onClick: onRequestDelete,
        title: 'Delete',
        type: ''
      },
      {
        onClick: onApprove,
        title: 'Approve',
        type: 'secondaryModified'
      },
      {
        onClick: onReject,
        title: 'Reject',
        type: 'secondaryModified'
      }
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'white',
        height: 'calc(100vh - 155px)',
        overflowY: 'scroll'
      }}
    >
      <InnerHeader subtitle={TrainingsRequestData?.paging?.total} title="Trainings" />
      <NoxTable
        actions={actions}
        columns={columns}
        data={requests}
        searchBy={SearchBy.TITLE}
        // @ts-ignore
        selectedRowKeys={selectedRowKeys}
        // @ts-ignore
        setSelectedRowKeys={setSelectedRowKeys}
      />
    </div>
  );
};

export default Trainings;
